import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AlertButton from "src/components/alerts/AlertButton";
import Breadcrumb, { Crumb } from "src/components/Breadcrumb";
import Button from "src/components/Button";
import Container from "src/components/layout/Container";
import NavItems from "src/components/layout/LayoutCompany/NavItems";
import NavbarToggle from "src/components/layout/NavbarToggle";
import ToggleButton from "src/components/layout/ToggleButton";
import UserProfileButton from "src/components/layout/UserProfileButton";
import Link from "src/components/Link";
import Logo from "src/components/Logo";
import { useParseBackUrl } from "src/hooks/useBackUrl";
import useElementSize from "src/hooks/useElementSize";
import useLayout from "src/hooks/useLayout";
import useScrollPosition from "src/hooks/useScrollPosition";
import useWindowSize from "src/hooks/useWindowSize";

const animationVariants = {
	open: {
		backgroundColor: "#18383B",
		color: "#fff",
	},
	closed: {
		backgroundColor: "#fff",
		boxShadow: "none",
	},
	"closed-shadow": {
		backgroundColor: "#fff",
		boxShadow: "none",
		// boxShadow: "0 1px 3px 0 rgba(0,0,0,.1),0 1px 2px -1px rgba(0,0,0,.1)",
	},
	scrolled: {
		backgroundColor: "rgba(255,255,255,0.9)",
		boxShadow: "0 1px 3px 0 rgba(0,0,0,.1),0 1px 2px -1px rgba(0,0,0,.1)",
	},
};

interface LayoutHeadProps {
	className?: string;
	actions?: any;
	title?: string;
	subtitle?: string;
	backUrl?: string;
	variant: "default" | "small" | "no-container";
	breadcrumb?: Crumb[];
	children?: any;
}

const LayoutHead = ({
	title,
	subtitle,
	className,
	actions,
	backUrl,
	variant,
	breadcrumb,
	children,
}: LayoutHeadProps) => {
	const { actions: layoutActions } = useLayout();
	const [visible, setVisible] = useState(false);
	const location = useLocation();
	const { isPhone, isTablet } = useWindowSize();
	const isDesktop = !(isPhone || isTablet);
	const { ref, size } = useElementSize();
	const y = useScrollPosition();
	const _backUrl = useParseBackUrl();
	const hasChildren = breadcrumb || children;
	const isShowbutton = isDesktop && (_backUrl || backUrl);
	const isScrolled = y > size.height;
	const getAnimationState = () => {
		if (visible) {
			return "open";
		}
		if (isScrolled) {
			return "scrolled";
		}

		if (!hasChildren) {
			return "closed-shadow";
		}

		return "closed";
	};

	useEffect(() => {
		layoutActions.setHead({
			size,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [size]);

	useEffect(() => {
		setVisible(false);
	}, [location]);

	const renderLogo = (color: "white" | "dark") => (
		<motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
			<Link to="/dashboard">
				<Logo
					{...{ color }}
					variant="small"
					className="w-8 h-8 block"
				/>
			</Link>
		</motion.div>
	);

	return (
		<>
			<motion.div
				initial={false}
				{...{ ref }}
				animate={getAnimationState()}
				variants={animationVariants}
				style={{
					zIndex: 94,
				}}
				className={`sticky top-0 py-4 ${!hasChildren && "mb-6 "} ${
					className || ""
				}`}
			>
				<NavbarToggle />
				<Container
					{...{ variant }}
					className="flex items-center justify-between"
				>
					<div className="flex items-start gap-6">
						<AnimatePresence>
							{!isDesktop
								? renderLogo(visible ? "white" : "dark")
								: false}
						</AnimatePresence>
						{isShowbutton && (
							<Button
								type="dark"
								iconOnly
								icon="fas fa-arrow-left"
								to={_backUrl || backUrl}
							></Button>
						)}
						<div className="flex flex-col text-dark">
							<div
								className={`flex ${
									isShowbutton && "h-[38px] items-center mb-1"
								}`}
							>
								{typeof title === "string" ? (
									<h1 className="text-[24px] hidden sm:flex lg:text-[32px]">
										{title}
									</h1>
								) : (
									title
								)}
							</div>
							{typeof subtitle === "string" ? (
								<small className="hidden sm:flex">
									{subtitle}
								</small>
							) : (
								subtitle
							)}
						</div>
					</div>
					<div className="flex items-center gap-6">
						<div className="items-center gap-6 hidden lg:flex">
							{actions}
						</div>
						{/* <LanguageSelector theme={"dark"} /> */}
						<AlertButton />
						<UserProfileButton />
						<div className="flex lg:hidden">
							<ToggleButton
								stroke={visible ? "#fff" : "#18383B"}
								onClick={() => setVisible(!visible)}
							/>
						</div>
					</div>
				</Container>
			</motion.div>
			{hasChildren && (
				<div className="bg-white shadow mb-6 md:mb-4 py-6">
					<Container>
						{children}
						{breadcrumb && (
							<Breadcrumb
								variant="dark"
								theme="bold"
								routes={breadcrumb}
							/>
						)}
					</Container>
				</div>
			)}
			<AnimatePresence>
				{visible && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-dark z-[80] flex flex-col flex-1"
						style={{
							paddingTop: size.top + size.height,
						}}
					>
						<div className="py-8 overflow-auto flex-1 flex flex-col">
							<NavItems />
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

LayoutHead.defaultProps = {
	variant: "default",
};

export default LayoutHead;
